/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef } from 'preact/hooks';
import { BaseLegend } from './BaseLegend';
import { useEvents } from './useEvents';
import { LegendProps } from './Legend.type';
import { getLegendNavUtils } from './LegendNavUtils';
import { getAriaProps, getDatatipText, getGridNums, getLegendTextStyle } from './LegendUtils';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useLegendTooltip } from './useLegendTooltip';
import { useItemFocus } from '../hooks/PRIVATE_useVisEvents';
import { useLegendLayout } from './useLegendLayout';
import { styles } from './themes/LegendStyles.css';
import { useCssVars } from '../hooks/PRIVATE_useCssVars';
import { legendVars } from './LegendUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { useTestId } from '../hooks/UNSAFE_useTestId';

/**
 * A legend displays an interactive description of symbols, colors, etc., used in graphical information representations.
 */
export function Legend<K extends string | number>({
  orientation = 'horizontal',
  items,
  isReadOnly = true,
  onItemAction,
  onInput,
  testId,
  ...props
}: LegendProps<K>) {
  const testIdProps = useTestId(testId);
  const rootRef = useRef<HTMLDivElement>(null);
  const rootDimsRef = useRef<DOMRect>();
  const { getItem, getDetailFromInfo, getPrevItemInfo, getNextItemInfo } = getLegendNavUtils(items);
  const { resolvedVars, cssContent } = useCssVars(legendVars);
  const legendFontStylesObj = getLegendTextStyle(
    {
      fontStyle: props.textFontStyle,
      fontSize: props.textFontSize?.toString(),
      fontWeight: props.textFontWeight?.toString(),
      fontFamily: props.textFontFamily
    },
    resolvedVars
  );
  const { textMeasureContent, layoutInfo } = useLegendLayout({
    resolvedVars,
    items,
    rootRef,
    rootDimsRef,
    orientation,
    legendFontStylesObj,
    symbolWidth: props.symbolWidth
  });
  const { hoveredItemInfo, focusedItemInfo, ...eventsProps } = useEvents<K>(
    !isReadOnly,
    props.hiddenIds != null,
    getDetailFromInfo,
    getPrevItemInfo,
    getNextItemInfo,
    onItemAction,
    onInput
  );

  const { focusedItemRef } = useItemFocus(focusedItemInfo);
  const datatipText = getDatatipText(items, getItem, focusedItemInfo, hoveredItemInfo);
  const { tooltipContent, tooltipProps } = useLegendTooltip({
    text: datatipText,
    rootDimsRef,
    focusedItemInfo,
    focusedItemRef
  });

  const ariaProps = getAriaProps(
    isReadOnly,
    props['aria-label'],
    props.hiddenIds,
    props['aria-describedby'],
    props['aria-labelledby']
  );
  const mergedProps = mergeProps(eventsProps, tooltipProps);
  const { itemsPerCol, itemsPerRow } = getGridNums(items.length, layoutInfo);
  const { legendStyle, renderedLegendStyle, preRenderedLegendStyle } = styles;
  const canRender = orientation === 'vertical' || !!layoutInfo;
  return (
    <div
      ref={rootRef}
      tabIndex={isReadOnly ? undefined : 0}
      role="application"
      class={classNames([legendStyle, canRender ? renderedLegendStyle : preRenderedLegendStyle])}
      {...testIdProps}
      {...ariaProps}
      {...mergedProps}>
      {canRender ? (
        <BaseLegend
          {...props}
          focusedItemRef={focusedItemRef}
          items={items}
          isReadOnly={isReadOnly}
          orientation={orientation}
          sectionIdx={0}
          itemsPerRow={itemsPerRow}
          itemsPerCol={itemsPerCol}
          itemWidth={layoutInfo?.isWrapped ? layoutInfo?.maxItemWidth : undefined}
          focusedItemInfo={focusedItemInfo}
          hoveredItemInfo={hoveredItemInfo}
          activeId={eventsProps['aria-activedescendant']}
        />
      ) : null}
      {tooltipContent}
      {textMeasureContent}
      {cssContent}
    </div>
  );
}
